import React from 'react';
import { Page, Text, View, Document, StyleSheet } from '@react-pdf/renderer';

const colors = {
    primary: '#3F51B5',
    darkText: '#1E293B',
    lightText: '#64748B',
    border: '#CBD5E1',
    white: '#FFFFFF',
    accentBg: '#F8FAFC'
};

const styles = StyleSheet.create({
    page: {
        padding: 30,
        fontFamily: 'Helvetica',
        color: colors.darkText,
        fontSize: 10,
        lineHeight: 1.5,
    },
    headerContainer: {
        borderBottomWidth: 2,
        borderBottomColor: colors.primary,
        paddingBottom: 8,
        marginBottom: 15,
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    companyName: {
        fontSize: 22,
        fontWeight: 'bold',
        color: colors.primary,
        letterSpacing: 1,
    },
    documentTitle: {
        fontSize: 10,
        color: colors.lightText,
        fontWeight: 'bold',
    },
    sectionTitle: {
        fontSize: 13,
        fontWeight: 'bold',
        color: colors.primary,
        marginTop: 10,
        marginBottom: 5,
        textTransform: 'uppercase',
    },
    bulletRow: {
        flexDirection: 'row',
        marginBottom: 8,
        alignItems: 'flex-start',
    },
    bulletPoint: {
        width: 12,
        fontSize: 11,
        color: colors.primary,
    },
    bulletContent: {
        flex: 1,
        textAlign: 'justify',
    },
    tableContainer: {
        marginTop: 5,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 4,
        overflow: 'hidden',
    },
    tableHeader: {
        flexDirection: 'row',
        backgroundColor: colors.primary,
        paddingVertical: 6,
        paddingHorizontal: 8,
        alignItems: 'center',
    },
    tableHeaderCell: {
        color: colors.white,
        fontWeight: 'bold',
        fontSize: 9,
    },
    tableRow: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: colors.border,
        paddingVertical: 5,
        paddingHorizontal: 8,
        alignItems: 'center',
    },
    tableCell: {
        fontSize: 9,
    },
    colName: { width: '30%', textTransform: 'uppercase' },
    colMin: { width: '12%', textAlign: 'center' },
    colOrder: { width: '15%', textAlign: 'center' },
    colMax: { width: '15%', textAlign: 'center' },
    colLot: { width: '16%', textAlign: 'center' },
    colMargin: { width: '12%', textAlign: 'right' },
    footer: {
        position: 'absolute',
        bottom: 20,
        left: 30,
        right: 30,
        textAlign: 'center',
        fontSize: 8,
        color: colors.lightText,
        borderTopWidth: 1,
        borderTopColor: colors.border,
        paddingTop: 5,
    }
});

// Helper utility function to slice data arrays into sub-arrays of exactly 20 items
const chunkArray = (array, size) => {
    const chunked = [];
    for (let i = 0; i < array.length; i += size) {
        chunked.push(array.slice(i, i + size));
    }
    return chunked;
};

// Standardized Table Fragment for a single chunk of 20 elements
const MarketTableChunk = ({ title, data, chunkIndex, totalChunks }) => {
    return (
        <View style={{ marginBottom: 15 }}>
            <Text style={styles.sectionTitle}>
                {title} {totalChunks > 1 ? `(Part ${chunkIndex + 1}/${totalChunks})` : ''}
            </Text>
            <View style={styles.tableContainer}>
                <View style={styles.tableHeader}>
                    <Text style={[styles.tableHeaderCell, styles.colName]}>Script Name</Text>
                    <Text style={[styles.tableHeaderCell, styles.colMin]}>Minimum</Text>
                    <Text style={[styles.tableHeaderCell, styles.colOrder]}>Order</Text>
                    <Text style={[styles.tableHeaderCell, styles.colMax]}>Maximum</Text>
                    <Text style={[styles.tableHeaderCell, styles.colLot]}>Market Lot</Text>
                    <Text style={[styles.tableHeaderCell, styles.colMargin]}>Margin</Text>
                </View>
                {data.map((row, index) => (
                    <View key={index} style={[styles.tableRow, { backgroundColor: index % 2 === 1 ? colors.accentBg : colors.white }]}>
                        <Text style={[styles.tableCell, styles.colName]}>{row.symbol_display || '-'}</Text>
                        <Text style={[styles.tableCell, styles.colMin]}>{row.min_qty || '1'}</Text>
                        <Text style={[styles.tableCell, styles.colOrder]}>{row.qty_per_order || '-'}</Text>
                        <Text style={[styles.tableCell, styles.colMax]}>{row.total_quantity || '-'}</Text>
                        <Text style={[styles.tableCell, styles.colLot]}>{row.lot_size || '-'}</Text>
                        <Text style={[styles.tableCell, styles.colMargin]}>{row.margin || '-'}</Text>
                    </View>
                ))}
            </View>
        </View>
    );
};

export default function SymbolPdf({ allSymbols = [], counts }) {
    const nsefuture = allSymbols.filter(item => item.type === 'nse');
    const nse_cash = allSymbols.filter(item => item.type === 'nse_cash');
    const nseoptions = allSymbols.filter(item => item.type === 'options');
    const mcxfuture = allSymbols.filter(item => item.type === 'mcx');
    const mcxoptions = allSymbols.filter(item => item.type === 'mcx_options');
    const sgx = allSymbols.filter(item => item.type === 'sgx');
    const crypto = allSymbols.filter(item => item.type === 'crypto_future');
    const forex = allSymbols.filter(item => item.type === 'forex_future');
    const comex = allSymbols.filter(item => item.type === 'comex_future');
    const index = allSymbols.filter(item => item.type === 'index');
    const us_stock = allSymbols.filter(item => item.type === 'us_stock');
    const dgcx = allSymbols.filter(item => item.type === 'dgcx');
    const ncdex = allSymbols.filter(item => item.type === 'ncdex');

    const appNameUpper = String(window.APP_NAME || 'TARGETEX').toUpperCase();

    // Apply filtering counts visualization constraints
    const visibleNse = counts?.nse === 1 ? nsefuture : nsefuture.slice(0, 15);
    const visibleNseCash = counts?.nse_cash === 1 ? nse_cash : nse_cash.slice(0, 15);
    const visibleOptions = counts?.options === 1 ? nseoptions : nseoptions.slice(0, 15);
    const visibleMcx = counts?.mcx === 1 ? mcxfuture : mcxfuture.slice(0, 15);
    const visibleMcxOptions = counts?.mcx_options === 1 ? mcxoptions : mcxoptions.slice(0, 15);
    const visibleSgx = counts?.sgx === 1 ? sgx : sgx.slice(0, 15);
    const visibleCrypto = counts?.crypto === 1 ? crypto : crypto.slice(0, 15);
    const visibleForex = counts?.forex === 1 ? forex : forex.slice(0, 15);
    const visibleComex = counts?.comex === 1 ? comex : comex.slice(0, 15);
    const visibleIndex = counts?.index === 1 ? index : index.slice(0, 15);
    const visibleUsStock = counts?.us_stock === 1 ? us_stock : us_stock.slice(0, 15);
    const visibleDgcx = counts?.dgcx === 1 ? dgcx : dgcx.slice(0, 15);
    const visibleNcdex = counts?.ncdex === 1 ? ncdex : ncdex.slice(0, 15);

    // Group all active configurations into an easily iteration-mappable scheme
    const marketSegments = [
        { title: "NSE Futures Specifications", data: visibleNse },
        { title: "NSE Cash Specifications", data: visibleNseCash },
        { title: "NSE Options Specifications", data: visibleOptions },
        { title: "MCX Specifications", data: visibleMcx },
        { title: "MCX Options Specifications", data: visibleMcxOptions },
        { title: "Index Specifications", data: visibleIndex },
        { title: "Comex Specifications", data: visibleComex },
        { title: "Forex Specifications", data: visibleForex },
        { title: "Crypto Specifications", data: visibleCrypto },
        { title: "SGX Specifications", data: visibleSgx },
        { title: "US Stock Specifications", data: visibleUsStock },
        { title: "DGCX Specifications", data: visibleDgcx },
        { title: "NCDEX Specifications", data: visibleNcdex }
    ];

    return (
        <Document>
            {/* PAGE 1: TERMS & CONDITIONS */}
            <Page size="A4" style={styles.page}>
                <View style={styles.headerContainer}>
                    <Text style={styles.companyName}>{appNameUpper}</Text>
                    <Text style={styles.documentTitle}>TERMS & CONDITIONS</Text>
                </View>

                <Text style={styles.sectionTitle}>Trading Rules & Violations</Text>
                <View style={styles.bulletRow}>
                    <Text style={styles.bulletPoint}>•</Text>
                    <Text style={styles.bulletContent}>Line trade (chamka), market manipulation, arbitrage & jobbing are strictly prohibited. We are continuously monitoring all trading activities. Suspicious trades related to these practices will be removed along with profits at any time during the running week or within 7 working days.</Text>
                </View>
                <View style={styles.bulletRow}>
                    <Text style={styles.bulletPoint}>•</Text>
                    <Text style={styles.bulletContent}>Scalping or squaring off positions within 5 minutes of opening will result in automated profit removal. Closing a position and opening the exact same position within 5 minutes must be avoided.</Text>
                </View>
                <View style={styles.bulletRow}>
                    <Text style={styles.bulletPoint}>•</Text>
                    <Text style={styles.bulletContent}>Latency trading & Robot trading are strictly prohibited. Current week's trades will be scrutinized by the surveillance team, and structural violations will cause all trades to be completely invalidated.</Text>
                </View>
                <View style={styles.bulletRow}>
                    <Text style={styles.bulletPoint}>•</Text>
                    <Text style={styles.bulletContent}>If multiple accounts are found operating from the same IP address or managed by a single individual exceeding market caps/max quantities, all trades will be voided and accounts will be suspended immediately.</Text>
                </View>
                <View style={styles.bulletRow}>
                    <Text style={styles.bulletPoint}>•</Text>
                    <Text style={styles.bulletContent}>Buy Stop / Sell Limit or Sell Stop / Buy Limit structures will not be tolerated. Traders using these system patterns risk immediate account suspension and complete trade invalidation.</Text>
                </View>

                <Text style={styles.sectionTitle}>Market Timings & Specifications</Text>
                <View style={styles.bulletRow}>
                    <Text style={styles.bulletPoint}>•</Text>
                    <Text style={styles.bulletContent}>Trading activates 1 minute after market opening and auto-terminates 1 minute before market closing. MCX window limits: 9:01 AM till 11:54 PM & NSE window limits: 9:16 AM to 3:29 PM.</Text>
                </View>
                <View style={styles.bulletRow}>
                    <Text style={styles.bulletPoint}>•</Text>
                    <Text style={styles.bulletContent}>Trading is completely barred on banned scripts; only close-only configurations are permitted. In validation disputes, structured logs will be provided; no platform arguments will be entertained.</Text>
                </View>
                <View style={styles.bulletRow}>
                    <Text style={styles.bulletPoint}>•</Text>
                    <Text style={styles.bulletContent}>The firm holds no liability for account losses tracing back to local/remote structural software issues. We heavily recommend updating your temporary access keys immediately.</Text>
                </View>

                <Text style={{ marginTop: 20, fontSize: 11, color: colors.primary, fontWeight: 'bold' }}>Thank You</Text>
                <Text style={styles.footer}>Page 1</Text>
            </Page>

            {/* DYNAMIC PAGINATION ENGINE FOR REMAINING DATA */}
            {marketSegments.map((segment) => {
                if (!segment.data || segment.data.length === 0) return null;

                // Chunk data into sets of 20 rows
                const chunks = chunkArray(segment.data, 20);

                return chunks.map((chunk, chunkIndex) => (
                    <Page size="A4" style={styles.page} key={`${segment.title}-${chunkIndex}`}>
                        <View style={styles.headerContainer}>
                            <Text style={styles.companyName}>{appNameUpper}</Text>
                            <Text style={styles.documentTitle}>EXCHANGE MARGIN SPECIFICATIONS</Text>
                        </View>

                        <MarketTableChunk
                            title={segment.title}
                            data={chunk}
                            chunkIndex={chunkIndex}
                            totalChunks={chunks.length}
                        />

                        <Text style={styles.footer}>Exchange Layout Summary</Text>
                    </Page>
                ));
            })}
        </Document>
    );
}